<script setup lang="ts">
import { FILE_INPUT_NAME } from "@/utilities/Client";
import ValidationMessage from "@/components/UI/ValidationMessage.vue";
import InstructionsButton from "@/components/UI/InfoModal/InstructionsButton.vue";
import InfoModal from "@/components/UI/InfoModal/InfoModal.vue";
import { computed, ref } from "vue";
import { useStore } from "vuex";

interface Props {
	isInPreview?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	isInPreview: false
});

const store = useStore();
const measurementSelectorVariables = computed(() => window.measurementSelectorVariables);
const { measurementsConfig, drawingRules, drawingExplainer, i18n } = measurementSelectorVariables.value;
const showDrawingRules = !!drawingRules;
const showDrawingExplanation = !!drawingExplainer;

const isHoveringFile = ref(false);
const fileUpload = ref(null);
const filename = computed(() => store.getters.getOriginalFilename);
const fileInputName = ref(FILE_INPUT_NAME);
const isUploading = computed(() => store.getters.getIsParsingFile);
const hasSucceeded = computed(() => store.getters.uploadedContent !== "");
const hasProblematicCorners = computed(() => store.getters.hasProblematicCorners);
const extendedProductInformation = computed(() => (window as any).extendedProductInformation || {});
const dxfUploadInfo = computed(() => extendedProductInformation.value.dxf_upload || []);
const showDxfInfo = computed(() => dxfUploadInfo.value && dxfUploadInfo.value.length > 0);

const allowed_mime_types = [
	'application/dxf',
	'application/x-autocad',
	'application/x-dxf',
	'drawing/x-dxf',
	'image/vnd.dxf',
	'image/x-autocad',
	'image/x-dxf',
	'zz-application/zz-winassoc-dxf',
];
const allowed_file_extensions = [
	'.dxf',
];

if ( measurementsConfig && measurementsConfig['base'] && measurementsConfig['base'].shape === "dxf") {
	filename.value = measurementsConfig['base'].dxfDefinition.original_filename;
}

function hasAllowedMimeType ( mimetype: string ): boolean {

	// dxf tend to miss mimetype, so we allow empty mimetype
	if ( mimetype.length === 0 ) {
		return true;
	}

	if ( allowed_mime_types.includes( mimetype ) ) {
		return true;
	}
	return false;
}

function handleFileUpload(): void {
	const files = fileUpload.value.files;

	if (files.length < 1) {
		return;
	}

	const file = files[0];

	if (!hasAllowedMimeType(file.type)) {
		return;
	}

	uploadFile(file);
}

function resetInput(): void {
	fileUpload.value.value = "";
}

function reset(): void {
	resetInput();

	store.commit('updateDxfDefinition', { svgPath: '' } );
	store.commit('setUploadedContent', {result: '', filename: ''} );
}

function setIsHoveringFile(value: boolean): void {
	isHoveringFile.value = value;
}

function processDroppedFile(event: DragEvent): void {

	// Put it in the input and use the same code path for both upload methods (drag, select file).
	fileUpload.value.files = event.dataTransfer.files;

	handleFileUpload();
}

const uploadFile = async (file: File): Promise<void> => {
	store.dispatch("updateUploadedContent", file);
};

function openModal(): void {
	store.commit('updateCurrentModalName', 'dxfUploadInfo');
}
</script>
<template>
	<div :class="['upload-container', { 'upload-container--preview': props.isInPreview }]">
		<a v-if="showDrawingRules && !props.isInPreview && !showDxfInfo" :href="drawingRules.url" target="_blank" class="text-link">
			<span aria-hidden="true" class="icon icon-info-sign"></span>
			{{ drawingRules.title }}
		</a>

		<div v-if="hasSucceeded">
			<div class="uploaded-file">
				<span>{{ $i18n.file }}</span>
				<strong>{{ filename }}</strong>
				<button @click.prevent="reset()">
					<span aria-hidden="true" class="icon icon-delete"></span>
					<span class="screen-reader-text">
						{{ $i18n.removeFile.replace("%s", filename) }}
					</span>
				</button>
			</div>
			<div>
				<portal-target name="shape-preview" />
			</div>
		</div>

		<div
			:class="{
				'file-dropbox': true,
				'file-dropbox--hovering': isHoveringFile,
			}"
			@dragover.prevent="setIsHoveringFile(true)"
			@dragenter.prevent="setIsHoveringFile(true)"
			@dragleave.prevent="setIsHoveringFile(false)"
			@dragend.prevent="setIsHoveringFile(false)"
			@dragdrop.prevent="setIsHoveringFile(false)"
			@drop.prevent="
				setIsHoveringFile(false);
				processDroppedFile($event);
			"
		>
			<p>
				{{ hasSucceeded ? $i18n.messages.dragNewFilesToUpload : $i18n.messages.dragFilesToUpload }}
				<span class="small">({{ allowed_file_extensions.join('/') }})</span>
				<br v-if="!props.isInPreview">
				<span>{{ (props.isInPreview ? ' ' : '') + $i18n.or }}</span>
			</p>

			<label for="measurements-selector-file-upload">
				<span :class="props.isInPreview ? 'text-link-button' : 'button button--blue'">{{ $i18n.selectFile }}</span>
			</label>
			<input
				class="file-upload"
				id="measurements-selector-file-upload"
				ref="fileUpload"
				:name="fileInputName"
				type="file"
				:accept="`${allowed_file_extensions.join(',')},${allowed_mime_types.join(',')}`"
				v-on:change="handleFileUpload"
				aria-describedby="measurements-selector-file-upload-validation-message"
			/>

			<div v-if="showDrawingExplanation && !props.isInPreview && !showDxfInfo" class="small">
				{{ $i18n.dxfExplainer }}<br>
				<a :href="drawingExplainer.url" target="_blank">
					{{ drawingExplainer.title }}
				</a>
			</div>
			<span v-if="isUploading">
				{{ $i18n.uploadingFile }}
			</span>
		</div>
		<validation-message
			v-if="hasProblematicCorners"
			type="notice"
		>
			{{ $i18n.problematicCornersNotice }}
		</validation-message>

		<div v-if="showDxfInfo && !props.isInPreview" class="upload-container__info-button">
			<div class="dxf-help-content">
				<span aria-hidden="true" class="icon icon-info-sign"></span>
				<div class="dxf-help-text">
					<div class="dxf-help-title">{{ $i18n.needHelp || 'Need help?' }}</div>
					<button type="button" class="dxf-help-link" @click.prevent="openModal">{{ $i18n.dxfHelpText || 'Bekijk onze handige hulpmiddelen voor het maken van een dxf bestand.' }}</button>
				</div>
			</div>
			<info-modal modal-name="dxfUploadInfo" :content="dxfUploadInfo"></info-modal>
		</div>
	</div>
</template>
<style scoepd lang="scss">
.upload-container {
	display: flex;
	flex-direction: column;
	gap: 16px;
	background-color: $brand-blue-background;
	border-radius: 8px;
	padding: 20px;

	&--preview {
		background-color: transparent;
		border-radius: 0;
		padding: 10px;
		height: 100%;
		justify-content: center;
	}

	&__info-button {
		display: flex;
		justify-content: center;
		margin-top: 10px;
		width: 100%;
	}
}

.dxf-help-content {
	display: flex;
	align-items: flex-start;
	gap: 10px;
	width: 100%;

	.icon {
		color: $brand-blue;
		font-size: 18px;
		flex-shrink: 0;
		margin-top: 2px;
	}
}

.dxf-help-link {
	background: transparent;
	border: none;
	cursor: pointer;
	text-align: left;
	padding: 0;
	font-weight: normal;
	font-size: 14px;
	color: $brand-blue-dark;
	text-decoration: underline;
	white-space: normal;
	word-break: break-word;
	hyphens: auto;

	&:hover {
		color: $brand-blue-dark;
		text-decoration: none;
	}
}

.dxf-help-text {
	display: flex;
	flex-direction: column;
	gap: 2px;
	flex: 1;
	min-width: 0;
}

.dxf-help-title {
	font-weight: bold;
	font-size: 14px;
	color: $brand-blue-dark;
	white-space: normal;
	word-break: break-word;
	hyphens: auto;
}

.dxf-help-subtitle {
	font-weight: normal;
	font-size: 14px;
	color: $brand-blue-dark;
	white-space: normal;
	word-break: break-word;
	hyphens: auto;
}

.small {
	@include font-size(12px);
}

.uploaded-file {
	@include font-size(12px);
	display: flex;
	border: 1px solid $border-color-blue;
	align-items: center;
	gap: 8px;
	padding-left: 8px;

	button {
		margin-left: auto;
		background-color: transparent;
		padding-block: 4px;
	}
}

.text-link {
	@include font-size(12px);
	color: $brand-blue-dark;
	display: flex;
	gap: 16px;
	align-items: center;
	font-weight: 600;

	.icon {
		@include font-size(18px);
	}
}

.text-link-button {
	color: #00ADEF;
	text-decoration: underline;
	font-weight: normal;
	cursor: pointer;
	font-size: 14px;

	&:hover {
		color: #00ADEF;
		text-decoration: none;
	}
}

.file-dropbox {
	display: flex;
	flex-direction: column;
	gap: 8px;
	border: 1px dashed $brand-blue-dark;
	border-radius: 8px;
	text-align: center;
	position: relative;
	padding: 10px;
	width: 100%;

	&--hovering {
		border-style: solid;
	}

	&--uploaded {
		border-style: none;
	}

	p {
		text-align: center;
		margin: 0;
	}

	.upload-container--preview & {
		background-color: white;
		border: 1px dashed $brand-blue-dark;
		padding: 20px;
		width: 288px;
		height: 120px;
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;

		p {
			color: #0074B5;
			font-weight: bold;
			font-size: 16px;
		}
	}
}

.file-upload {
	cursor: pointer;
	opacity: 0;
	width: 0;
	height: 0;
}
</style>
